import { Link } from '@tanstack/react-router';

import {
  formatClockMinutesTo12Hour,
  TIMELINE_BLOCK_INSET_PX,
} from '../lib/timeline';
import { OVERVIEW_EVENT_TYPE, type OverviewEvent } from '../lib/types';

interface ClassScheduleBlockProps {
  event: OverviewEvent;
  leftPx: number;
  widthPx: number;
}

const isPrivateLessonEvent = (event: OverviewEvent) =>
  event.type === OVERVIEW_EVENT_TYPE.PRIVATE_LESSON;

const getBlockTitle = (event: OverviewEvent, timeRange: string) => {
  const lines = [event.className, timeRange, event.instructorName];
  if (event.studentName) {
    lines.push(event.studentName);
  }
  return lines.join('\n');
};

export function ClassScheduleBlock({
  event,
  leftPx,
  widthPx,
}: ClassScheduleBlockProps) {
  const timeRange = `${formatClockMinutesTo12Hour(event.startMinutes)} - ${formatClockMinutesTo12Hour(event.endMinutes)}`;
  const privateLesson = isPrivateLessonEvent(event);

  return (
    <div
      className={[
        'absolute overflow-hidden rounded-md border px-1.5 py-1',
        privateLesson ? 'border-sky-300' : 'border-border',
      ].join(' ')}
      style={{
        left: leftPx,
        width: widthPx,
        top: TIMELINE_BLOCK_INSET_PX,
        bottom: TIMELINE_BLOCK_INSET_PX,
        backgroundColor: event.color,
      }}
      title={getBlockTitle(event, timeRange)}
    >
      <div className='flex h-full min-w-0 flex-col justify-center gap-0.5 leading-snug'>
        {privateLesson ? (
          <Link
            to='/classes/private-lessons'
            className='w-fit truncate text-sm font-medium leading-snug hover:underline'
            title={event.className}
          >
            {event.className}
          </Link>
        ) : (
          <Link
            to='/classes/$classId/enrolled-students'
            params={{ classId: event.classId }}
            className='w-fit truncate text-sm font-medium leading-snug hover:underline'
            title={event.className}
          >
            {event.className}
          </Link>
        )}
        <div className='truncate text-xs leading-snug text-muted-foreground'>
          {timeRange}
        </div>
        {event.studentName ? (
          <div
            className='truncate text-xs font-medium leading-snug text-foreground/80'
            title={event.studentName}
          >
            {event.studentName}
          </div>
        ) : null}
        <div className='truncate text-xs leading-snug text-muted-foreground'>
          {event.instructorName}
        </div>
      </div>
    </div>
  );
}
